/**
 * classPosts/{id} — per-class feed (Phase 4). A teacher (or the Director) posts
 * to one class; the class's families get notified and can read it. This is the
 * "Miss Claire posts, the class's parents see it" surface, distinct from the
 * staff-only studio board and the participant-scoped DMs.
 *
 * Each post can carry an ackRequired flag plus per-recipient acks{} and
 * dismissedBy{} maps — the same "stays on your attention pane until you act"
 * pattern the Studio Board uses. Replies live in a flat subcollection. The
 * viewer-scoped reply snapshots are the source of truth for counts; the
 * parent keeps only whole-class lastReplyAt activity for inbox ordering.
 */
import { useEffect, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  getCountFromServer,
  getDocFromServer,
  getDocsFromServer,
  limit as limitDocs,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp as FirestoreTimestamp,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { isPermissionDenied } from './jrStaffReadsClient';
import { useAuth } from './auth-context';
import { useReadEpoch } from './readEpoch';
import { classPostAckExempt, classPostAckPending } from './classPostAckCore.ts';
import { isStaffRole, useUserDoc } from './users';
import {
  notifyUsers,
  type NotifyDeliveryResult,
  type NotifyOptions,
  type NotifyPayload,
} from './notifications';
import type { MessageAttachment } from './threads';
import { saveClassPostMessage, saveClassPostReplyMessage } from './messageAttachmentWrite';
import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import type { VideoMirrorBlockedDetail } from './messageAttachmentWrite';
import {
  jrStaffFamilyUidsForOffering,
  mergeReservationFamilyUids,
} from './classFamilyAudience';
import { classPostInboxRawFetchLimit, collapseClassPostInboxWindow } from './messageInbox';
import { isHiddenFixturePost } from './smokeFixture';

export const CLASS_POST_TYPE_LABELS = {
  update: 'Class update',
  postCompNote: 'Post-comp note',
} as const;

export type ClassPostType = keyof typeof CLASS_POST_TYPE_LABELS;

/**
 * Who can see a reply. Bobbie's decision: "I think the parent should have
 * the option that it's private to Kim and the teacher, just to Kim, or the
 * whole class." Missing on older replies means 'class' in the app's own
 * reasoning, but the security rule cannot retroactively apply that default
 * to a query filter (a missing field never matches a '==' or 'in' filter),
 * so pre-existing replies are only readable by the Director and their own
 * author until they're touched again. See the rules comment in
 * shared/firestore.rules for the full tradeoff note.
 */
export const REPLY_VISIBILITY_LABELS = {
  class: 'Whole class',
  // Bobbie: "It can just be teacher, not teacher plus Ms. Kim." The routing is
  // unchanged (this still reaches the teacher and the Director); the label just
  // says the person you are actually writing to.
  teacher: 'Teacher',
  director: 'Ms. Kim',
} as const;

export type ReplyVisibility = keyof typeof REPLY_VISIBILITY_LABELS;

export const CLASS_POST_REPLY_TEXT_MAX = 4000;
export const CLASS_POST_REPLY_READ_MAX = 100;

function isWellFormedDocumentIdUnicode(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      return false;
    }
  }
  return true;
}

export function isSafeFirestoreDocumentId(value: unknown): value is string {
  if (typeof value !== 'string' || !value || value === '.' || value === '..' || value.includes('/')) {
    return false;
  }
  if (!isWellFormedDocumentIdUnicode(value)) return false;
  // Keep this identical to the reply gateway and notification authority.
  // Class source IDs also ride FCM data payloads, whose measured safe contract
  // is 65 UTF-8 bytes. Reject an over-limit segment everywhere rather than
  // accepting it here and slicing it later. Count bytes, not UTF-16 units.
  return new TextEncoder().encode(value).length <= 65;
}

export function classPostReplyTextError(text: string): string | null {
  return plainMessageText(text).trim().length > CLASS_POST_REPLY_TEXT_MAX
    ? `Keep replies to ${CLASS_POST_REPLY_TEXT_MAX.toLocaleString()} characters or fewer.`
    : null;
}

export function replyVisibilityLockLabel(
  visibility: ReplyVisibility | undefined
): string | null {
  if (visibility === 'teacher') return 'Teacher + Kim only';
  if (visibility === 'director') return 'Kim only';
  return null;
}
/**
 * How many dates one class post may carry, per the rules' own limit.
 *
 * KNOWN BUG, PRE-DATES THIS FILE'S CURRENT SHAPE: the rule says twenty, but a
 * class post carrying THREE or more of these is rejected outright today. The
 * create rule validates each entry unrolled, inside a single evaluation that
 * Firestore caps at 1000 expressions, and the rest of the shape check already
 * spends most of that budget. Going over the cap surfaces as a bare
 * PERMISSION_DENIED, not a validation message, so the post just refuses to
 * save and the caller reports a generic failure.
 *
 * It bites Parent Watch Week hardest: buildImportantDates() emits one entry per
 * class meeting in the watch week, so any class meeting three or more times
 * that week silently fails to receive its note while its neighbours succeed.
 * Measured against origin/hub-staging: 0, 1 and 2 entries save; 3+ deny.
 *
 * Not fixed here - finding which sub-expression eats the budget is its own
 * piece of work. Pinned by "class posts hit a rules expression cliff" in
 * hub/tests/firestore-rules.test.mjs so the threshold cannot quietly get worse.
 */
export const CLASS_POST_MAX_IMPORTANT_DATES = 20;

/**
 * The class-post edit bounds, kept identical to the server's own numbers in
 * netlify/functions/classPostRecord.mjs. MAX_BODY counts the VISIBLE words;
 * MAX_STORED_BODY bounds the string that is actually stored, because a
 * formatted post serialises to several times the text a person sees.
 */
export const CLASS_POST_MAX_BODY = 20000;
export const CLASS_POST_MAX_STORED_BODY = 250000;
/**
 * A parent's edit of her own class post is held to the 5000 visible
 * characters her post was created under (familyClassPost.mjs), with the shared
 * stored ceiling for that number. Same numbers as FAMILY_MAX_BODY and
 * storedMessageCeiling(FAMILY_MAX_BODY) in classPostRecord.mjs, so an edit the
 * server would refuse is refused here first, without the round trip. Written
 * as plain numbers, not a call, because several test harnesses load this file
 * with a stand-in messageFormatting that carries plainMessageText alone;
 * tests/class-post-record.test.mjs proves both equal the server's.
 */
export const CLASS_POST_FAMILY_MAX_BODY = 5000;
export const CLASS_POST_FAMILY_MAX_STORED_BODY = 125000;

export interface ClassPostImportantDate {
  id: string;
  title: string;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  note?: string | null;
}

export interface ClassPostDoc {
  id: string;
  offeringId: string;
  /** Linked dancer proving a family author belongs to this class. */
  familyStudentId?: string | null;
  authorUid: string;
  authorName: string;
  /** Server-stamped when the author used a parent-managed dancer login. */
  authorType?: 'dancer';
  /** Server-stamped dancer identity for supervised class-board messages. */
  dancerStudentId?: string;
  body: string;
  /** Update category. Missing on older docs means standard class update. */
  postType?: ClassPostType;
  /** When true, recipients must Acknowledge or Dismiss to clear it. */
  ackRequired?: boolean;
  /** Student ids this post targets. Missing or empty means whole class. */
  studentIds?: string[];
  /** Dates the post should surface in calendars and message summaries. */
  importantDates?: ClassPostImportantDate[];
  /** Family user ids this post was sent to. Staff can read all class posts. */
  expectedRecipientUids?: string[];
  /**
   * Recipients who arrived AFTER the post was written, added back by the
   * enrollment repair in netlify/functions/setEnrollment.mjs so a family
   * linked on Tuesday can read Monday's post at all. Server-owned: no client
   * rule branch can write this key.
   *
   * They can read it. They owe nothing on it. Bobbie, 2026-09-01: "They
   * should also not have to acknowledge old posts" - so every ack surface
   * subtracts these uids before it asks anyone for anything or shows Kim who
   * has not answered. classPostAckExempt and classPostAckAudienceUids below
   * are the two predicates that do it; use them rather than reading this
   * array directly.
   */
  backfilledRecipientUids?: string[];
  /** Per-recipient acknowledgement timestamps, keyed by uid. */
  acks?: Record<string, Timestamp | FieldValue>;
  /** Per-recipient dismiss timestamps, keyed by uid. */
  dismissedBy?: Record<string, Timestamp | FieldValue>;
  /** Facebook-style reactions: one emoji per uid (see REACTION_EMOJI). */
  reactions?: Record<string, string>;
  /**
   * A DANCER'S reaction, one emoji per dancer login uid, written ONLY by
   * reactToDancerClassPost (hub/netlify/functions/dancerPortal.mjs) - never
   * this client. Deliberately a separate field from `reactions` above:
   * Bobbie, 2026-08-31, ruled the reaction visible to family, teachers, and
   * other students, but "don't take it away from parents also needing to
   * see the post" - a dancer reaction must never read as an acknowledgment.
   * Keeping it out of `reactions` (which reactToClassPost can stamp into
   * `acks` in the same write) and out of `acks`/`dismissedBy` entirely is
   * what makes that structural rather than a filter someone has to
   * remember. Read-only here: no client write path exists for this field,
   * and shared/firestore.rules never lists it in a create/update allowlist.
   */
  dancerReactions?: Record<string, string>;
  /** Direct video attachment URL (parent comp-board uploads, storage-hosted). */
  videoUrl?: string | null;
  /** Photo attached by staff, shown inline in the class feed and message. */
  imageUrl?: string | null;
  /** Photos, videos, and safe files shared with this class conversation. */
  attachments?: MessageAttachment[];
  /**
   * Pinned to the top of the class page. Pins are TIMED: a teacher picks how
   * long when she pins, and the pin expires on its own. See pinnedUntil.
   */
  pinned?: boolean;
  /**
   * When the pin lapses, or null for "until I unpin it". This is a derived
   * read, not a scheduled job: an expired pin simply stops sorting to the top
   * the next time the page loads (bulletins do the same thing with expiresAt).
   * Rules cap it at 120 days out so nobody pins something to the year 3000.
   */
  pinnedUntil?: Timestamp | null;
  /** Most-recent whole-class reply time. Restricted reply activity stays private. */
  lastReplyAt?: Timestamp | null;
  /** Set when the author edits the post after publishing. */
  editedAt?: Timestamp | FieldValue;
  createdAt: Timestamp | FieldValue;

  // --- Un-post + the permanent record (see docs/parent-watch-week-next.md) ---
  /**
   * The one-tap send this post came from, when it was part of one. Lets a
   * fan-out to seventy class boards be found again, edited, or taken back
   * without opening seventy boards by hand.
   */
  broadcastId?: string | null;
  /**
   * How many times this post has been edited or removed. The server writes
   * revision `r{previousCount}` and bumps this by one in the same transaction,
   * so the count and the kept versions cannot come apart. A gap in the run
   * would mean something wrote outside that path. See classPostRevisionId().
   */
  revisionCount?: number;
  /** Reply edits/retractions kept under this post's revisions collection. */
  replyRevisionCount?: number;
  /** When the post was last edited or removed. Drives the staff history view. */
  lastChangedAt?: Timestamp | FieldValue;
  /**
   * Set when the post is taken back. The body is deliberately left alone:
   * un-posting hides a post from families, it never destroys what was sent.
   */
  retractedAt?: Timestamp | FieldValue | null;
  retractedBy?: string;
  retractedByName?: string;
  /**
   * Who the post reached before it was taken back. Retracting empties
   * expectedRecipientUids (that is what actually drops it out of every
   * family's feed, enforced by rules) so the original list is kept here.
   */
  originalRecipientUids?: string[];
}

/**
 * One prior version of a class post, kept forever.
 *
 * Written only by the classPostRecord gateway for posts and the
 * messageAttachmentWrite gateway for replies, in the same transaction as the
 * change each record describes. No client can create, edit or delete one.
 */
export interface ClassPostRevisionDoc {
  id: string;
  /** Missing on historical whole-post revisions. */
  recordType?: 'post' | 'reply';
  /** Present only when recordType is reply. */
  replyId?: string;
  replyAuthorName?: string;
  /** The post or reply body as it stood BEFORE this change. */
  body: string;
  changeKind: 'edit' | 'retract';
  changedBy: string;
  changedByName: string;
  changedAt: Timestamp | FieldValue;
}

/** Revision id for the change taking a post from `previousCount` to the next. */
export function classPostRevisionId(previousCount: number): string {
  return `r${previousCount}`;
}

/** True once a post has been taken back. Its body is still intact for staff. */
export function isClassPostRetracted(post: Pick<ClassPostDoc, 'retractedAt'>): boolean {
  return Boolean(post.retractedAt);
}

/** How long a pin lasts. Timed pins expire on their own, no job required. */
export const PIN_DURATION_LABELS = {
  '7': '1 week',
  '14': '2 weeks',
  '30': '1 month',
  forever: 'Until I unpin it',
} as const;

export type PinDuration = keyof typeof PIN_DURATION_LABELS;

/** Rules refuse a pin further out than this, so nothing pins itself forever. */
export const PIN_MAX_DAYS = 120;

/** The moment a pin of this length lapses, or null for "until I unpin it". */
export function pinExpiryDate(duration: PinDuration, from = new Date()): Date | null {
  if (duration === 'forever') return null;
  const days = Number(duration);
  if (!Number.isFinite(days) || days <= 0) return null;
  const out = new Date(from.getTime());
  out.setDate(out.getDate() + days);
  return out;
}

/**
 * Is this post pinned RIGHT NOW? A pin with no end date runs until somebody
 * unpins it; a timed pin simply stops counting once its date has passed.
 * Guarded through toMillis so a pending server timestamp cannot throw.
 */
export function isClassPostPinned(
  post: Pick<ClassPostDoc, 'pinned' | 'pinnedUntil'>,
  nowMs = Date.now()
): boolean {
  if (!post.pinned) return false;
  const until = post.pinnedUntil;
  const ms =
    until && typeof (until as { toMillis?: unknown }).toMillis === 'function'
      ? (until as Timestamp).toMillis()
      : null;
  return ms == null || ms > nowMs;
}

/**
 * Pin or unpin a post. Only the class's teacher or the Director may call it,
 * and the rule accepts nothing but these two fields changing.
 */
export async function setClassPostPin(
  postId: string,
  pinned: boolean,
  pinnedUntil: Date | null
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'classPosts', postId), {
    pinned,
    pinnedUntil: pinned && pinnedUntil ? FirestoreTimestamp.fromDate(pinnedUntil) : null,
  });
}

export interface ClassPostReplyDoc {
  id: string;
  authorUid: string;
  authorName: string;
  text: string;
  /** Photos, videos, and safe files shared with this reply. */
  attachments?: MessageAttachment[];
  /** Who can see this reply. Missing on older replies (treated as 'class' in the UI). */
  visibility?: ReplyVisibility;
  createdAt: Timestamp | FieldValue;
  /** Set when the author fixed a typo. Absent on every reply never edited. */
  editedAt?: Timestamp | FieldValue | null;
  /** Set when the author took it back. The row stays and reads as withdrawn,
   *  the same way a retracted class post does, so a thread never answers a
   *  sentence that silently vanished. */
  retractedAt?: Timestamp | FieldValue | null;
  retractedByUid?: string | null;
  retractedByName?: string | null;
  /** Number of prior reply versions kept in the parent post's history. */
  revisionCount?: number;
}

export interface UseClassPostsResult {
  loading: boolean;
  posts: ClassPostDoc[];
  error: string | null;
  /**
   * True once the raw read actually hit its own limit (the query's own
   * doc count, before withoutRetracted() trims it). An older post beyond that
   * limit could exist and never surface in a search that assumes this list is
   * the whole history.
   */
  capped?: boolean;
  /** Number of contiguous newest posts in the bounded page. */
  recentPostCount?: number;
  /** True when a notification-linked older post is merged outside that page. */
  includedLinkedPost?: boolean;
}

export function classPostType(post: Pick<ClassPostDoc, 'postType'>): ClassPostType {
  return post.postType === 'postCompNote' ? 'postCompNote' : 'update';
}

export function classPostTypeLabel(post: Pick<ClassPostDoc, 'postType'>): string {
  return CLASS_POST_TYPE_LABELS[classPostType(post)];
}

function postMatchesStudent(
  post: Pick<ClassPostDoc, 'studentIds'>,
  studentId: string | undefined
): boolean {
  if (!studentId) return true;
  const studentIds = post.studentIds ?? [];
  return studentIds.length === 0 || studentIds.includes(studentId);
}

function cleanClassPostImportantDates(
  dates: ClassPostImportantDate[] | undefined
): ClassPostImportantDate[] {
  return (dates ?? [])
    .filter((date) => date && typeof date.title === 'string' && typeof date.date === 'string')
    .map((date) => {
      const title = date.title.trim().slice(0, 120);
      const isoDate = date.date.trim();
      return {
        id: (date.id || `${isoDate}-${title}`).trim().slice(0, 80),
        title,
        date: isoDate,
        startTime: date.startTime?.trim() || null,
        endTime: date.endTime?.trim() || null,
        note: date.note?.trim().slice(0, 300) || null,
      };
    })
    .filter((date) => date.title && /^\d{4}-\d{2}-\d{2}$/.test(date.date))
    .slice(0, CLASS_POST_MAX_IMPORTANT_DATES);
}

/** One explicit page of a class feed; the UI can ask for more up to the cap. */
export const CLASS_POST_FEED_PAGE_SIZE = 100;
/** Hard browser boundary for one mounted class history. */
export const CLASS_POST_FEED_MAX_POSTS = 500;

/** Subscribe to a bounded class-post page, newest first. */
export function useClassPosts(
  offeringId: string | undefined,
  maxPosts = CLASS_POST_FEED_PAGE_SIZE,
  includePostId?: string | null
): UseClassPostsResult {
  const { user } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const userUid = user?.uid;
  const userRole = userDoc?.role;
  const userStatus = userDoc?.status;
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);
  const [recentPostCount, setRecentPostCount] = useState(0);
  const [includedLinkedPost, setIncludedLinkedPost] = useState(false);
  const [resolvedFeedKey, setResolvedFeedKey] = useState<string | null>(null);
  const linkedPostId = isSafeFirestoreDocumentId(includePostId) ? includePostId : null;
  const effectiveMaxPosts = Math.min(
    CLASS_POST_FEED_MAX_POSTS,
    Math.max(1, Number.isSafeInteger(maxPosts) ? maxPosts : CLASS_POST_FEED_PAGE_SIZE)
  );
  const activeFeedKey = !meLoading && db && offeringId && userUid && userRole && userStatus === 'active'
    ? JSON.stringify([offeringId, userUid, userRole, userStatus, effectiveMaxPosts, linkedPostId])
    : null;

  useEffect(() => {
    if (meLoading) return;
    if (!activeFeedKey || !db || !offeringId || !userUid || !userRole) {
      setPosts([]);
      setCapped(false);
      setRecentPostCount(0);
      setIncludedLinkedPost(false);
      setResolvedFeedKey(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setPosts([]);
    setCapped(false);
    setRecentPostCount(0);
    setIncludedLinkedPost(false);
    const base = collection(db, 'classPosts');
    const q = isStaffRole(userRole)
      ? query(
          base,
          where('offeringId', '==', offeringId),
          ...(userRole === 'director' ? [] : [where('retractedAt', '==', null)]),
          orderBy('createdAt', 'desc'),
          limitDocs(effectiveMaxPosts + 1)
        )
      : query(
          base,
          where('offeringId', '==', offeringId),
          where('expectedRecipientUids', 'array-contains', userUid),
          orderBy('createdAt', 'desc'),
          limitDocs(effectiveMaxPosts + 1)
        );
    let pagePosts: ClassPostDoc[] = [];
    let includedPost: ClassPostDoc | null = null;
    let pageReady = false;
    let pageFailed = false;
    let disposed = false;
    let includedUnsub: (() => void) | null = null;
    const publish = () => {
      // The bounded feed is independently useful. Never hold it in Loading
      // while an optional exact old-post server read is slow or offline;
      // publish the page first and merge the linked post if it later settles.
      if (!pageReady || pageFailed) return;
      const byId = new Map(pagePosts.map((post) => [post.id, post]));
      const targetWasOutsidePage = !!includedPost && !byId.has(includedPost.id);
      if (includedPost) byId.set(includedPost.id, includedPost);
      setRecentPostCount(pagePosts.length);
      setIncludedLinkedPost(targetWasOutsidePage);
      setPosts([...byId.values()].sort((a, b) => {
        const aMs = a.createdAt && 'toMillis' in a.createdAt ? a.createdAt.toMillis() : 0;
        const bMs = b.createdAt && 'toMillis' in b.createdAt ? b.createdAt.toMillis() : 0;
        return bMs - aMs;
      }));
      setResolvedFeedKey(activeFeedKey);
      setLoading(false);
    };
    const pageUnsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        // Firestore persistent cache is not re-authorized by current rules.
        // Never render a class post until this listener reaches the server.
        if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        pagePosts = snap.docs
          .slice(0, effectiveMaxPosts)
          .map((d) => ({ id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) }));
        setCapped(snap.docs.length > effectiveMaxPosts);
        setError(null);
        pageReady = true;
        publish();
      },
      (err) => {
        pageFailed = true;
        pagePosts = [];
        includedPost = null;
        setPosts([]);
        setCapped(false);
        setRecentPostCount(0);
        setIncludedLinkedPost(false);
        setError(err.message);
        setResolvedFeedKey(activeFeedKey);
        setLoading(false);
      }
    );
    if (linkedPostId) {
      const linkedRef = doc(db, 'classPosts', linkedPostId);
      const applyLinkedPost = (snap: Awaited<ReturnType<typeof getDocFromServer>>) => {
        includedPost = snap.exists()
          ? { id: snap.id, ...(snap.data() as Omit<ClassPostDoc, 'id'>) }
          : null;
        if (includedPost?.offeringId !== offeringId
            || (userRole !== 'director' && includedPost?.retractedAt != null)) {
          includedPost = null;
        }
        publish();
      };
      // Force the exact old post through current server rules. A cached post
      // from a prior Director or broader-role session must not flash after a
      // demotion, re-scope, or offline restart. Once server-confirmed, attach
      // a live document listener and ignore its cache snapshots. A later
      // retraction or audience revocation therefore removes the separately
      // merged post without ever reviving privileged cached data.
      void getDocFromServer(linkedRef).then(
        (snap) => {
          if (disposed) return;
          if (!snap.metadata.fromCache && !snap.metadata.hasPendingWrites) {
            applyLinkedPost(snap);
          }
          includedUnsub = onSnapshot(
            linkedRef,
            { includeMetadataChanges: true },
            (liveSnap) => {
              if (disposed || liveSnap.metadata.fromCache || liveSnap.metadata.hasPendingWrites) return;
              applyLinkedPost(liveSnap);
            },
            () => {
              if (disposed) return;
              includedPost = null;
              publish();
            }
          );
        },
        () => {
          // Missing, denied, offline and wrong-class targets are deliberately
          // indistinguishable and never take down the ordinary feed.
          if (disposed) return;
          includedPost = null;
          publish();
        }
      );
    }
    return () => {
      disposed = true;
      pageUnsub();
      includedUnsub?.();
    };
  }, [activeFeedKey, effectiveMaxPosts, linkedPostId, offeringId, meLoading, userRole, userUid]);

  const awaitingActiveFeed = activeFeedKey !== null && resolvedFeedKey !== activeFeedKey;
  if (activeFeedKey === null) {
    return {
      loading: meLoading,
      posts: [],
      error: null,
      capped: false,
      recentPostCount: 0,
      includedLinkedPost: false,
    };
  }
  return {
    loading: loading || awaitingActiveFeed,
    posts: awaitingActiveFeed ? [] : posts,
    error: awaitingActiveFeed ? null : error,
    capped: awaitingActiveFeed ? false : capped,
    recentPostCount: awaitingActiveFeed ? 0 : recentPostCount,
    includedLinkedPost: awaitingActiveFeed ? false : includedLinkedPost,
  };
}

/** Recent posts across all offerings tied to one dancer, newest first. */
export function useDancerClassPosts(
  offeringIds: string[],
  maxPosts = 8,
  studentId?: string
): UseClassPostsResult {
  const { user } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const userUid = user?.uid;
  const userRole = userDoc?.role;
  const userStatus = userDoc?.status;
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedDancerFeedKey, setResolvedDancerFeedKey] = useState<string | null>(null);
  // Firestore document IDs may contain ordinary delimiter characters. Keep
  // the dependency key structurally encoded so ['a|b'] never aliases
  // ['a', 'b'] and reuses the prior viewer's rows for one render.
  const stableOfferingKey = JSON.stringify([...offeringIds].sort());
  const activeDancerFeedKey = !meLoading && db && userUid && userRole && userStatus === 'active'
    && offeringIds.length > 0
    ? JSON.stringify([stableOfferingKey, maxPosts, studentId ?? null, userUid, userRole, userStatus])
    : null;

  useEffect(() => {
    if (meLoading) return;
    if (!activeDancerFeedKey || !db || !userUid || !userRole) {
      setPosts([]);
      setResolvedDancerFeedKey(null);
      setLoading(false);
      return;
    }

    const ids = JSON.parse(stableOfferingKey) as string[];
    if (ids.length === 0) {
      setPosts([]);
      setResolvedDancerFeedKey(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setPosts([]);
    const base = collection(db, 'classPosts');
    const offeringSet = new Set(ids);
    const byId = new Map<string, ClassPostDoc>();
    const publish = () => {
      setPosts(
        withoutRetracted([...byId.values()])
          .sort((a, b) => {
            const aMs = a.createdAt && 'toMillis' in a.createdAt ? a.createdAt.toMillis() : 0;
            const bMs = b.createdAt && 'toMillis' in b.createdAt ? b.createdAt.toMillis() : 0;
            return bMs - aMs;
          })
          .slice(0, maxPosts)
      );
      setError(null);
      setResolvedDancerFeedKey(activeDancerFeedKey);
      setLoading(false);
    };

    if (!isStaffRole(userRole)) {
      const q = query(
        base,
        where('expectedRecipientUids', 'array-contains', userUid),
        orderBy('createdAt', 'desc'),
        limitDocs(50)
      );
      const unsub = onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snap) => {
          if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
          byId.clear();
          snap.docs.forEach((d) => {
            const post = { id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) };
            if (offeringSet.has(post.offeringId) && postMatchesStudent(post, studentId)) {
              byId.set(post.id, post);
            }
          });
          publish();
        },
        (err) => {
          byId.clear();
          setPosts([]);
          setError(err.message);
          setResolvedDancerFeedKey(activeDancerFeedKey);
          setLoading(false);
        }
      );
      return () => unsub();
    }

    let remainingInitialSnapshots = ids.length;
    let failed = false;
    const unsubs = ids.map((offeringId) => {
      const q = query(
        base,
        where('offeringId', '==', offeringId),
        ...(userRole === 'director' ? [] : [where('retractedAt', '==', null)]),
        orderBy('createdAt', 'desc'),
        limitDocs(maxPosts)
      );
      return onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snap) => {
          if (failed || snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
          [...byId.values()]
            .filter((post) => post.offeringId === offeringId)
            .forEach((post) => byId.delete(post.id));
          snap.docs.forEach((d) => {
            const post = { id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) };
            if (postMatchesStudent(post, studentId)) byId.set(d.id, post);
          });
          if (remainingInitialSnapshots > 0) remainingInitialSnapshots -= 1;
          if (remainingInitialSnapshots === 0) publish();
        },
        (err) => {
          if (failed) return;
          failed = true;
          byId.clear();
          setPosts([]);
          setError(err.message);
          setResolvedDancerFeedKey(activeDancerFeedKey);
          setLoading(false);
        }
      );
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [activeDancerFeedKey, maxPosts, meLoading, stableOfferingKey, studentId, userRole, userUid]);

  const awaitingActiveDancerFeed = activeDancerFeedKey !== null
    && resolvedDancerFeedKey !== activeDancerFeedKey;
  if (activeDancerFeedKey === null) {
    return { loading: meLoading, posts: [], error: null };
  }
  return {
    loading: loading || awaitingActiveDancerFeed,
    posts: awaitingActiveDancerFeed ? [] : posts,
    error: awaitingActiveDancerFeed ? null : error,
  };
}

/**
 * Drop taken-back posts from staff-facing lists.
 *
 * Families never see them anyway: retracting empties expectedRecipientUids, so
 * their array-contains queries stop matching. Non-Director staff queries now
 * prove `retractedAt == null`; this client boundary ships before the matching
 * read rule is narrowed. The local filter remains defense in depth.
 */
function withoutRetracted(posts: ClassPostDoc[]): ClassPostDoc[] {
  return posts.filter((post) => !isClassPostRetracted(post));
}

/** The Messages inbox read's own limit - exported so a caller can name the
 *  exact number in an honest "matches show from the newest N" caveat rather
 *  than repeating the literal 50. */
export const CLASS_POST_INBOX_CAP = 50;

/**
 * FamilyCommandCenter.tsx's own window for `useClassPostInbox`, wider than
 * the default so a family's home card sees deeper into a busy studio night
 * than the plain Messages list needs to. Exported so
 * familyWaitingOnYou.ts's badge listener (D4 round seven, REFUTE_msg2_R6.md
 * P2-B) can query the SAME window rather than a second, unbounded read - an
 * ack-required post older than this cap must be equally invisible to the
 * badge and the card, never counted on one and unreachable on the other.
 */
export const FAMILY_CLASS_POST_ACK_WINDOW = 80;

/** Class and comp-dance posts that should appear in the broader Messages list. */
/**
 * `enabled = false` opens no listener and returns an empty, settled result:
 * Messages mounts a second, deeper copy of this read only while Past is open
 * or a search is running (DECISIONS 182, Bobbie 2026-11-03).
 */
export function useClassPostInbox(maxPosts = CLASS_POST_INBOX_CAP, enabled = true): UseClassPostsResult {
  const { user } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const userUid = user?.uid;
  const userRole = userDoc?.role;
  const userStatus = userDoc?.status;
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracked off the RAW snapshot doc count (before withoutRetracted() trims
  // it), the same way useArchivedBulletins reads its own cap - a retracted
  // post trimmed out of `posts` must not make an actually-capped read look
  // complete.
  const [capped, setCapped] = useState(false);
  const readEpoch = useReadEpoch();
  const [resolvedInboxKey, setResolvedInboxKey] = useState<string | null>(null);
  const activeInboxKey = enabled && !meLoading && db && userUid && userRole && userStatus === 'active'
    ? JSON.stringify([userUid, userRole, userStatus, maxPosts])
    : null;

  useEffect(() => {
    if (meLoading) return;
    if (!activeInboxKey || !db || !userUid || !userRole) {
      setPosts([]);
      setCapped(false);
      setResolvedInboxKey(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setPosts([]);
    setCapped(false);
    const base = collection(db, 'classPosts');
    const staffRead = isStaffRole(userRole);
    // Staff reads scan further than they show (see
    // classPostInboxRawFetchLimit's own comment) so the collapse below has
    // room to fold a whole broadcast send into one slot before the display
    // cap applies. The family read is unaffected by any of this: it is
    // already scoped to array-contains this one uid, so a studio-wide send
    // can add at most as many raw docs as that family has enrolled classes,
    // never seventy.
    const rawFetchLimit = classPostInboxRawFetchLimit(maxPosts);
    const q = staffRead
      ? query(
          base,
          ...(userRole === 'director' ? [] : [where('retractedAt', '==', null)]),
          orderBy('createdAt', 'desc'),
          limitDocs(rawFetchLimit)
        )
      : query(
          base,
          where('expectedRecipientUids', 'array-contains', userUid),
          orderBy('createdAt', 'desc'),
          limitDocs(maxPosts)
        );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        const live = withoutRetracted(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) }))
        );
        if (staffRead) {
          // The test classes' own notes never reach a real staff inbox
          // (smokeFixture.ts). The robot family reads the family branch.
          const real = live.filter((post) => !isHiddenFixturePost(post));
          const { posts: windowed, capped: windowCapped } = collapseClassPostInboxWindow(real, maxPosts);
          setPosts(windowed);
          // Capped when there were more distinct sends than the window
          // shows, OR when the raw read itself hit its own ceiling - the
          // second case means a group at the edge of the window may not
          // even have its full membership yet, so this cannot claim
          // completeness either way. Never off the raw doc count alone
          // (the old formula): that undercounted capped once collapsing
          // began doing real work, since far fewer than `maxPosts` raw docs
          // can still be `maxPosts`-or-more distinct GROUPS.
          setCapped(windowCapped || snap.docs.length >= rawFetchLimit);
        } else {
          setPosts(live);
          setCapped(snap.docs.length >= maxPosts);
        }
        // Without this the Command Center's "Waiting on people" tile keeps
        // saying the read failed while good posts arrive behind it.
        setError(null);
        setResolvedInboxKey(activeInboxKey);
        setLoading(false);
      },
      (err) => {
        setPosts([]);
        setCapped(false);
        setError(err.message);
        setResolvedInboxKey(activeInboxKey);
        setLoading(false);
      }
    );
    return () => unsub();
    // readEpoch: the way back from a terminal listener error. See
    // lib/readEpoch.ts.
  }, [activeInboxKey, maxPosts, meLoading, readEpoch, userRole, userUid]);

  const awaitingActiveInbox = activeInboxKey !== null && resolvedInboxKey !== activeInboxKey;
  if (activeInboxKey === null) {
    return { loading: meLoading, posts: [], error: null, capped: false };
  }
  if (awaitingActiveInbox) {
    return { loading: true, posts: [], error: null, capped: false };
  }
  return { loading, posts, error, capped };
}

export interface UseClassPostRepliesResult {
  loading: boolean;
  replies: ClassPostReplyDoc[];
  error: string | null;
  /** Exact count of every reply this viewer may read, separate from content. */
  exactCount: number | null;
  /** True when content is limited to the newest safe window. */
  contentCapped: boolean;
  /** A directly-linked readable reply was older than the content window. */
  includedLinkedReply: boolean;
}

/** The disclosure copy is derived only from server-returned readable docs. */
export function classPostReplyDisclosureLabel(
  expanded: boolean,
  result: UseClassPostRepliesResult
): string {
  const action = expanded ? 'Hide' : 'Show';
  if (result.loading || result.error || result.exactCount == null) return `${action} replies`;
  const count = result.exactCount;
  return `${action} ${count} ${count === 1 ? 'reply' : 'replies'}`;
}

/** Honest bounded-content copy, including the aggregate-failure fallback. */
export function classPostReplyWindowLabel(result: UseClassPostRepliesResult): string | null {
  if (!result.contentCapped) return null;
  const base = result.exactCount == null
    ? `Showing the newest ${CLASS_POST_REPLY_READ_MAX} readable replies.`
    : `Showing the newest ${CLASS_POST_REPLY_READ_MAX} of ${result.exactCount.toLocaleString()} readable replies.`;
  return result.includedLinkedReply
    ? `${base} A linked older reply is shown separately.`
    : base;
}

/**
 * Subscribe to a post's replies, oldest first.
 *
 * Reply visibility ('class' | 'teacher' | 'director') is resource-dependent,
 * so a bare unfiltered list is only provable for the Director (see the rules
 * comment in shared/firestore.rules). Everyone else merges two or three
 * provable slices, same pattern as useAllBulletins()/useDiscussions():
 *   - Director: one bare query, everything.
 *   - Assigned teacher of this post's class: visibility in ['class','teacher']
 *     plus their own authorUid=='me' slice (covers any 'director' reply they wrote).
 *   - Everyone else (staff not assigned to the class, and family): visibility=='class'
 *     plus their own authorUid=='me' slice.
 *
 * Pass offeringTeacherUids (the parent class's complete teaching team) so the
 * hook can tell whether the signed-in user is the assigned teacher.
 */
export function useClassPostReplies(
  postId: string | undefined,
  offeringTeacherUids: readonly string[] = [],
  includeReplyId?: string
): UseClassPostRepliesResult {
  const { user } = useAuth();
  const { userDoc, loading: userLoading } = useUserDoc();
  const userUid = user?.uid;
  const userRole = userDoc?.role;
  const userStatus = userDoc?.status;
  const teacherKey = JSON.stringify([...offeringTeacherUids].sort());
  const linkedReplyId = isSafeFirestoreDocumentId(includeReplyId) ? includeReplyId : null;
  const activePostKey = postId
    ? JSON.stringify([
        postId,
        userUid ?? '',
        userRole ?? '',
        userStatus ?? '',
        userLoading ? 'loading' : 'ready',
        teacherKey,
        linkedReplyId ?? '',
      ])
    : null;
  const [replies, setReplies] = useState<ClassPostReplyDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exactCount, setExactCount] = useState<number | null>(null);
  const [contentCapped, setContentCapped] = useState(false);
  const [includedLinkedReply, setIncludedLinkedReply] = useState(false);
  const [resolvedPostKey, setResolvedPostKey] = useState<string | null>(null);

  useEffect(() => {
    if (userLoading) return;
    setReplies([]);
    setError(null);
    setExactCount(null);
    setContentCapped(false);
    setIncludedLinkedReply(false);
    if (!db || !postId || !userUid || !userRole || userStatus !== 'active') {
      setResolvedPostKey(activePostKey);
      setLoading(false);
      return;
    }

    setLoading(true);
    const base = collection(db, 'classPosts', postId, 'replies');
    const isAssignedTeacher =
      isStaffRole(userRole) && offeringTeacherUids.includes(userUid);

    const contentQueries =
      userRole === 'director'
        ? [query(base, orderBy('createdAt', 'asc'), limitToLast(CLASS_POST_REPLY_READ_MAX + 1))]
        : isAssignedTeacher
          ? [
              query(base, where('visibility', 'in', ['class', 'teacher']), orderBy('createdAt', 'asc'), limitToLast(CLASS_POST_REPLY_READ_MAX + 1)),
              query(base, where('authorUid', '==', userUid), orderBy('createdAt', 'asc'), limitToLast(CLASS_POST_REPLY_READ_MAX + 1)),
            ]
          : [
              query(base, where('visibility', '==', 'class'), orderBy('createdAt', 'asc'), limitToLast(CLASS_POST_REPLY_READ_MAX + 1)),
              query(base, where('authorUid', '==', userUid), orderBy('createdAt', 'asc'), limitToLast(CLASS_POST_REPLY_READ_MAX + 1)),
            ];

    // Exact counts use inclusion-exclusion over the same viewer-readable
    // union as the content queries. The own-all term intentionally includes
    // the caller's legacy replies with no visibility field.
    const countTerms = userRole === 'director'
      ? [{ q: query(base), multiplier: 1 }]
      : isAssignedTeacher
        ? [
            { q: query(base, where('visibility', 'in', ['class', 'teacher'])), multiplier: 1 },
            { q: query(base, where('authorUid', '==', userUid)), multiplier: 1 },
            {
              q: query(
                base,
                where('authorUid', '==', userUid),
                where('visibility', 'in', ['class', 'teacher'])
              ),
              multiplier: -1,
            },
          ]
        : [
            { q: query(base, where('visibility', '==', 'class')), multiplier: 1 },
            { q: query(base, where('authorUid', '==', userUid)), multiplier: 1 },
            {
              q: query(
                base,
                where('authorUid', '==', userUid),
                where('visibility', '==', 'class')
              ),
              multiplier: -1,
            },
          ];

    const sliceDocs = contentQueries.map(() => new Map<string, ClassPostReplyDoc>());
    const sliceCapped = contentQueries.map(() => false);
    const seeded = contentQueries.map(() => false);
    let failed = false;
    let disposed = false;
    let countGeneration = 0;
    let linkedReply: ClassPostReplyDoc | null = null;
    let linkedReplyUnsub: (() => void) | null = null;

    const refreshExactCount = async () => {
      const generation = ++countGeneration;
      try {
        const snapshots = await Promise.all(countTerms.map((term) => getCountFromServer(term.q)));
        const count = Math.max(0, snapshots.reduce(
          (total, snapshot, index) => total + snapshot.data().count * countTerms[index].multiplier,
          0
        ));
        if (disposed || generation !== countGeneration) return;
        setExactCount(count);
        setContentCapped(count > CLASS_POST_REPLY_READ_MAX);
      } catch {
        // Content remains usable. A missing/denied aggregate stays generic
        // rather than guessing a count or exposing another visibility slice.
        if (disposed || generation !== countGeneration) return;
        setExactCount(null);
      }
    };

    const publish = () => {
      // The normal bounded thread must never wait on the optional targeted
      // server read. Publish as soon as its live slices seed, then merge the
      // linked reply later if that independent read settles. A stalled or
      // offline deep-link fetch cannot hold an otherwise readable thread in
      // Loading forever.
      if (failed || !seeded.every(Boolean)) return;
      const byId = new Map<string, ClassPostReplyDoc>();
      for (const docs of sliceDocs) {
        for (const [id, reply] of docs) byId.set(id, reply);
      }
      const docs = Array.from(byId.values()).sort((a, b) => {
        const at = (a.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
        const bt = (b.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
        return at - bt;
      });
      const newest = docs.slice(-CLASS_POST_REPLY_READ_MAX);
      const linkedIsOlder = linkedReply != null && !newest.some((reply) => reply.id === linkedReply?.id);
      const visible = linkedIsOlder && linkedReply
        ? [...newest, linkedReply].sort((a, b) => {
            const at = (a.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
            const bt = (b.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
            return at - bt;
          })
        : newest;
      setReplies(visible);
      setIncludedLinkedReply(linkedIsOlder);
      setContentCapped(sliceCapped.some(Boolean) || docs.length > CLASS_POST_REPLY_READ_MAX);
      setResolvedPostKey(activePostKey);
      setLoading(false);
    };

    const unsubs = contentQueries.map((q, i) => {
      return onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snap) => {
          if (failed || disposed || snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
          const next = new Map<string, ClassPostReplyDoc>();
          for (const d of snap.docs) {
            next.set(d.id, { id: d.id, ...(d.data() as Omit<ClassPostReplyDoc, 'id'>) });
          }
          sliceDocs[i] = next;
          sliceCapped[i] = snap.docs.length > CLASS_POST_REPLY_READ_MAX;
          seeded[i] = true;
          publish();
          if (seeded.every(Boolean)) void refreshExactCount();
        },
        (listenerError) => {
          if (failed || disposed) return;
          failed = true;
          setReplies([]);
          setExactCount(null);
          setContentCapped(false);
          setIncludedLinkedReply(false);
          setError(listenerError.message || 'Replies are unavailable.');
          setResolvedPostKey(activePostKey);
          setLoading(false);
        }
      );
    });

    if (linkedReplyId) {
      const linkedReplyRef = doc(base, linkedReplyId);
      const applyLinkedReply = (snapshot: Awaited<ReturnType<typeof getDocFromServer>>) => {
        linkedReply = snapshot.exists()
          ? { id: snapshot.id, ...(snapshot.data() as Omit<ClassPostReplyDoc, 'id'>) }
          : null;
        publish();
      };
      // A targeted private reply must never come from a persistent browser
      // cache left by an earlier Director session. Force a server read so the
      // current user's rules and current role decide whether it exists. A
      // cache-suppressed live listener then removes it on a later visibility,
      // parent-retraction, or audience change.
      void getDocFromServer(linkedReplyRef).then(
        (snapshot) => {
          if (disposed) return;
          if (!snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites) {
            applyLinkedReply(snapshot);
          }
          linkedReplyUnsub = onSnapshot(
            linkedReplyRef,
            { includeMetadataChanges: true },
            (liveSnapshot) => {
              if (disposed || liveSnapshot.metadata.fromCache || liveSnapshot.metadata.hasPendingWrites) return;
              applyLinkedReply(liveSnapshot);
            },
            () => {
              if (disposed) return;
              linkedReply = null;
              publish();
            }
          );
        },
        () => {
          // Missing and unreadable are deliberately identical. The normal
          // bounded thread still loads and no linked-reply fact is exposed.
          if (disposed) return;
          linkedReply = null;
          publish();
        }
      );
    }

    return () => {
      disposed = true;
      countGeneration += 1;
      unsubs.forEach((unsub) => unsub());
      linkedReplyUnsub?.();
    };
  }, [activePostKey, linkedReplyId, postId, teacherKey, userLoading, userRole, userStatus, userUid]);

  // Key the resolved snapshot to the exact active query. This is render-pure
  // and StrictMode-safe: the first render for a newly activated card cannot
  // reuse an old empty or stale result while its effects are still pending.
  const awaitingActivePost = activePostKey !== null && resolvedPostKey !== activePostKey;
  if (activePostKey === null) {
    return {
      loading: false,
      replies: [],
      error: null,
      exactCount: null,
      contentCapped: false,
      includedLinkedReply: false,
    };
  }
  return {
    loading: loading || awaitingActivePost,
    replies: awaitingActivePost ? [] : replies,
    error: awaitingActivePost ? null : error,
    exactCount: awaitingActivePost ? null : exactCount,
    contentCapped: awaitingActivePost ? false : contentCapped,
    includedLinkedReply: awaitingActivePost ? false : includedLinkedReply,
  };
}

export interface OfferingForPost {
  id: string;
  name: string;
  studentIds: string[];
}

export interface CreateClassPostOptions {
  studentIds?: string[];
  /** Dates the post should surface in calendars and message summaries. */
  importantDates?: ClassPostImportantDate[];
  /**
   * The class media announcement's clip, and nothing else.
   *
   * MediaPanel and the class page auto-post "New video posted: <label>" the
   * moment the link is saved on the offering, and pass the clip's URL so the
   * feed card plays it inline. The gateway accepts this ONLY when the URL is
   * already one of that offering's stored videoLinks entries, checked in the
   * write transaction - see the classPost branch of
   * netlify/functions/lib/messageAttachmentWrite.mjs. A URL that is not on
   * the class is refused there, never silently dropped from the saved post.
   * But the two auto-announce callers (MediaPanel.tsx, Offering.tsx) treat
   * this whole call as best-effort and catch that refusal into a bare
   * console.error, so the announcement post itself still disappears without
   * telling the teacher.
   *
   * There is deliberately no imageUrl twin: no caller had one, and a photo
   * belongs in `attachments`, where the gateway reserves it.
   */
  videoUrl?: string | null;
  /** Photos, videos, or files uploaded for this class-board message. */
  attachments?: MessageAttachment[];
  /** Stamp this post as part of a one-tap send, so it can be taken back later. */
  broadcastId?: string | null;
  /**
   * False only when a higher-level studio action already sends the one alert
   * for these durable per-class copies.
   */
  notifyFamilies?: boolean;
  /** Pin this post to the top of the class page. */
  pinned?: boolean;
  /** When the pin lapses. null with pinned means "until I unpin it". */
  pinnedUntil?: Date | null;
  onNotify?: ClassPostNotifyReport;
  /**
   * Fires when this post carried a video that could not reach its class's
   * Music & videos because that shelf already held 200 entries
   * (REFUTE_walls_R2 finding 2: the write result carried
   * `videoMirrorBlocked` all the way to the wire and stopped there, so a
   * teacher's video could go quiet with nobody told). The post itself still
   * saved; only the shelf copy did not land.
   *
   * `wasReplay` (REFUTE_walls_R7 P3-4) is true when this fired from the
   * REPLAY branch (`created === false` - the same post, a retry after an
   * earlier response was lost, P2-3) rather than the write that first
   * produced the block. A caller's notice must phrase the fact in the past
   * tense for a replay, never claim the shelf IS full right now, which a
   * replay never re-checks.
   *
   * `blockedCount` and `partial` (REFUTE_walls_R9 P3-4): how many of the
   * post's own video attachments did not land, and whether some of them
   * DID (a PARTIAL block, round ten's Codex P2-B fix) rather than none at
   * all (a TOTAL one). A caller's notice must never treat those the same -
   * a partial block never suggests reposting, since the clip already on
   * the shelf would land a second time.
   */
  onVideoMirrorBlocked?: (detail: VideoMirrorBlockedDetail) => void;
}

/**
 * How the notify step reports back. Fires EXACTLY ONCE per post, always.
 * `null` means nothing could be confirmed: the notify call threw, or there
 * was nobody to tell. `detail` carries the reason for the console.
 */
export type ClassPostNotifyReport = (
  result: NotifyDeliveryResult | null,
  detail?: string
) => void;

type QueuedClassPostNotifyResult = NotifyDeliveryResult & {
  /** Additive while older notify clients roll forward. */
  queued?: boolean;
  notificationOperationId?: string;
};

function durableClassPostOperationId(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function queuedClassPostNotifyResult(operationId: string): QueuedClassPostNotifyResult {
  return {
    inApp: 0, email: 0, sms: 0, push: 0, skipped: 0, errors: 0,
    serverReached: true, queued: true, notificationOperationId: operationId,
  };
}

/**
 * Tell the class's families about a new post, and REPORT what happened.
 *
 * This used to be an inline try/catch that swallowed the failure and only
 * called back on the happy path. That is how a teacher could write
 * "rehearsal moves to 6pm on Saturday", read "Posted to the Hub and sent
 * notifications", and have literally nothing go out: with the notify
 * function down, or notification creates denied at the rules layer, the
 * callback simply never fired and the composer printed its hardcoded
 * success line anyway.
 *
 * So: one report, every time, and the composer decides what to say from it.
 * `send` is injectable so the contract can be proven without a live
 * Firestore.
 */
export async function reportClassPostNotify(
  recipientUids: string[],
  payload: NotifyPayload,
  onNotify?: ClassPostNotifyReport,
  send: typeof notifyUsers = notifyUsers,
  // WHY A CLASS POST NOW NAMES ITS OWN AUTHORITY (Bobbie, 2026-08-26: "Jr
  // staff should be able to alert Their class!").
  //
  // Sending with no authority put this in notify.mjs's 'unscoped' studio
  // lane, which is gated on STUDIO_ROLES - director, manager, company, foh.
  // `ensemble` is deliberately absent, because that lane carries
  // caller-supplied copy to caller-supplied uids and a Jr Staff account
  // should not hold that. The silent cost was that a Jr Staff teacher's
  // class post notified NOBODY, with no error anywhere: probed at
  // company 2 rows, foh 2 rows, ensemble 0.
  //
  // Passing { classPost: { id } } routes it through the 'class' lane
  // instead, which is role-free but far stricter about audience: the server
  // re-reads the post and only builds a row for a recipient already in its
  // own expectedRecipientUids, and only when the post's authorUid matches
  // the caller. So the audience gets NARROWER and server-proved while the
  // capability gets wider. An outsider stays refused either way.
  //
  // Optional, and the existing delivery tests deliberately still call this
  // without it: an omitted authority keeps the old unscoped behaviour for
  // any caller that has not opted in.
  authority?: NotifyOptions
): Promise<void> {
  if (recipientUids.length === 0) {
    onNotify?.(null, 'no linked family accounts to alert');
    return;
  }
  try {
    // Send first, THEN report: an optional call never evaluates its arguments,
    // so wrapping the await inside it sent nothing for a callbackless caller.
    const result = await send(recipientUids, payload, authority ?? {});
    onNotify?.(result);
  } catch (e) {
    onNotify?.(null, e instanceof Error ? e.message : String(e));
  }
}

/**
 * Post to a class feed and notify the targeted students' families. Author must
 * be the class's teacher or the Director (enforced by rules too). Family
 * resolution reads the students collection (staff/Director can) and collects
 * familyUids for the selected students.
 */
export function useCreateClassPost() {
  const { user } = useAuth();
  return async (
    offering: OfferingForPost,
    body: string,
    ackRequired = false,
    postType: ClassPostType = 'update',
    options: CreateClassPostOptions = {}
  ): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = body.trim();
    if (!trimmed) throw new Error('Write something first.');
    if (plainMessageText(trimmed).length > 4000) {
      throw new Error('Shorten the message to 4,000 visible characters.');
    }

    // REFUTE_walls_R4 finding 3: a source-shape test alone could still be
    // satisfied by a comment, an aliased import, or a whole new file the
    // scan never walks - five of seven tried evasions got past it. This is
    // the STRUCTURAL guard instead: a caller cannot post attachments
    // through this hook without ALSO handing it onVideoMirrorBlocked, full
    // stop, no per-file judgment call about whether this particular post
    // "could" be blocked. Gated off in production so a caller this check
    // itself has not caught yet still posts rather than fails outright for
    // a real family mid-post; every dev session and every test run - which
    // is where a missing wire actually gets caught - throws immediately.
    if (
      Array.isArray(options.attachments) && options.attachments.length > 0
      && typeof options.onVideoMirrorBlocked !== 'function'
      && process.env.NODE_ENV !== 'production'
    ) {
      throw new Error(
        'useCreateClassPost: attachments were passed with no onVideoMirrorBlocked. '
        + 'Every caller that can post a video through this hook must handle a full '
        + "class shelf blocking it, even if this particular call site believes it "
        + 'never can. See tests/create-class-post-video-mirror-wiring.test.mjs.'
      );
    }

    const offeringStudentIds = Array.from(new Set(offering.studentIds ?? []));
    const hasExplicitTargets = Array.isArray(options.studentIds) && options.studentIds.length > 0;
    const requestedStudentIds = hasExplicitTargets ? options.studentIds ?? [] : offeringStudentIds;
    const offeringStudentSet = new Set(offeringStudentIds);
    const targetStudentIds = Array.from(
      new Set(requestedStudentIds.filter((sid) => offeringStudentSet.has(sid)))
    ).sort();
    const sids = new Set(targetStudentIds);
    const familyUids = new Set<string>();
    if (sids.size > 0) {
      // A Jr Staff is refused the students collection since B2. This list is
      // only the composer's fallback (the server derives the real audience
      // in the write transaction and its list wins), so on that refusal it
      // stays empty rather than guessing a wider audience for a targeted post.
      try {
        const studs = await getDocsFromServer(collection(db, 'students'));
        studs.forEach((d) => {
          if (sids.has(d.id)) {
            const fam = (d.data().familyUids ?? []) as string[];
            fam.forEach((u) => familyUids.add(u));
          }
        });
      } catch (err) {
        if (!isPermissionDenied(err)) throw err;
      }
    }
    // The Jr Staff lane, the same one bookings take (lib/classFamilyAudience).
    // A dancer who TEACHES or ASSISTS this class is not on its roster, so
    // their parent was picked up by nothing here and never saw the message or
    // the dates it carries. Bobbie, 2026-08-27: "Messages and rehearsals. Not
    // showing up on the calendar for them and it needs too."
    //
    // WHOLE-CLASS POSTS ONLY. When the composer targets specific dancers
    // (hasExplicitTargets), the post is addressed to those families and
    // nobody else - widening it to the assistant's parent would hand a note
    // meant for two households to a third. A Jr Staff parent belongs in the
    // audience for what the CLASS is told, not for what one family is told.
    const jrStaffFamilyUids = hasExplicitTargets
      ? []
      : await jrStaffFamilyUidsForOffering(offering.id);
    let expectedRecipientUids = mergeReservationFamilyUids([...familyUids], jrStaffFamilyUids);
    const importantDates = cleanClassPostImportantDates(options.importantDates);

    // The class media announcement's clip. The gateway checks it against the
    // offering's own stored videoLinks; see CreateClassPostOptions.videoUrl.
    const announcedVideoUrl =
      typeof options.videoUrl === 'string' && options.videoUrl.startsWith('https://')
        ? options.videoUrl
        : null;
    const broadcastId =
      typeof options.broadcastId === 'string' && options.broadcastId
        ? options.broadcastId.slice(0, 65)
        : null;

    // A pin further out than the rules' cap would be rejected server-side, so
    // clamp here and tell the truth on the card rather than failing the send.
    const pinned = options.pinned === true;
    const maxPin = new Date();
    maxPin.setDate(maxPin.getDate() + PIN_MAX_DAYS - 1);
    const requestedPinUntil = pinned ? (options.pinnedUntil ?? null) : null;
    const pinnedUntil =
      requestedPinUntil && requestedPinUntil.getTime() > Date.now()
        ? new Date(Math.min(requestedPinUntil.getTime(), maxPin.getTime()))
        : null;

    /* ONE WRITE PATH, AND IT IS THE SERVER'S.
       This used to branch: a post carrying an attachment went through the
       gateway, and a text-only post was written straight from the browser
       with setDoc. That browser write is what the 2026-09-04 audit found:
       the rules type-checked expectedRecipientUids and authorName and bound
       neither, so a console could address every family in the studio under
       the Director's name. Both fields are now derived in the write
       transaction, from the offering's roster and the caller's live user
       doc, and the browser cannot state either one.

       The audience computed above is still worth computing: it is what the
       composer shows and the fallback the notify step uses if the server
       answers without a list. The server's list wins whenever it arrives. */
    const ref = doc(collection(db, 'classPosts'));
    const result = await saveClassPostMessage({
      user,
      targetId: offering.id,
      writeId: ref.id,
      attachments: options.attachments ?? [],
      payload: {
        body: trimmed,
        postType,
        ackRequired,
        studentIds: hasExplicitTargets ? targetStudentIds : [],
        importantDates,
        broadcastId,
        pinned,
        pinnedUntil,
        ...(options.notifyFamilies === false ? { notifyFamilies: false } : {}),
        ...(announcedVideoUrl ? { videoUrl: announcedVideoUrl } : {}),
      },
    }, {
      // REFUTE_walls_R6 P3-1: saveClassPostMessage now carries its own
      // structural guard too - this hook's own guard above (finding 3)
      // proves ITS caller handed over a callback; this is that same
      // callback, threaded one layer further down so the gateway's own
      // guard sees it too, rather than tripping on a false positive here.
      onVideoMirrorBlocked: options.onVideoMirrorBlocked,
    });
    expectedRecipientUids = result.expectedRecipientUids ?? expectedRecipientUids;
    // REFUTE_walls_R2 finding 2 / REFUTE_walls_R6 P3-1: saveClassPostMessage
    // itself now calls onVideoMirrorBlocked when the result carries the
    // flag (passed through above), fired synchronously before notify, so
    // the poster sees it regardless of whether the notify step below
    // succeeds. Calling it again here would fire it twice for the same post.

    // Notify the class's families. The post is already saved, so a failure
    // here is not a failed post - but it is not a success either, and the
    // composer has to be able to say which. See reportClassPostNotify.
    const notificationOperationId = durableClassPostOperationId(result.notificationOperationId);
    if (notificationOperationId) {
      // The write transaction created or verified this exact operation. Its
      // worker owns alert delivery, so a second browser notify would duplicate
      // the alert. This reports acceptance, not provider delivery.
      options.onNotify?.(queuedClassPostNotifyResult(notificationOperationId));
    } else if (options.notifyFamilies !== false) {
      await reportClassPostNotify(
        expectedRecipientUids,
        {
          kind: 'message',
          title:
            postType === 'postCompNote'
              ? `${offering.name}: post-comp notes`
              : `${offering.name}: new post`,
          body: trimmed.slice(0, 120),
          link: `/class/${offering.id}#class-post-${ref.id}`,
        },
        options.onNotify,
        undefined,
        // The server derives the real title and body from this post and its
        // offering; the payload above is the fallback for the unscoped lane.
        { classPost: { id: ref.id } }
      );
    } else {
      options.onNotify?.(null, 'family alerts were intentionally suppressed');
    }
    return ref.id;
  };
}

const CLASS_POST_RECORD_FN = '/.netlify/functions/classPostRecord';

export interface ClassPostRecordResult {
  changed: boolean;
  /** Family alert rows removed, so the withdrawn wording leaves their phones. */
  notificationsCleared: number;
  /** Set when the post was taken back but its old alerts could not be cleared. */
  warning?: string;
}

/**
 * Ask the server to reword or take back a post.
 *
 * These two writes do NOT happen in the browser, and the rules deny them to
 * every client. A Firestore rule can only see one write at a time, so it can
 * never require that the "here is the wording being replaced" record was
 * written alongside the change - which is exactly the guarantee Bobbie asked
 * for. The server writes both in one transaction, so the old wording cannot be
 * lost or faked. See hub/netlify/functions/classPostRecord.mjs.
 */
async function callClassPostRecord(
  action: 'edit' | 'retract',
  postId: string,
  body?: string
): Promise<ClassPostRecordResult> {
  const data = (await postToRecordFn({
    action,
    postId,
    ...(body === undefined ? {} : { body }),
  })) as { changed?: boolean; notificationsCleared?: number; warning?: string };
  return {
    changed: Boolean(data.changed),
    notificationsCleared: data.notificationsCleared ?? 0,
    ...(data.warning ? { warning: data.warning } : {}),
  };
}

/** Shared POST + error mapping for every classPostRecord action. */
async function postToRecordFn(payload: Record<string, unknown>): Promise<unknown> {
  const me = auth?.currentUser;
  if (!me) throw new Error('Sign in required.');
  const idToken = await me.getIdToken();
  let res: Response;
  try {
    res = await fetch(CLASS_POST_RECORD_FN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error('Could not reach the studio server. Nothing was changed.');
  }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown> & {
    ok?: boolean;
    reason?: string;
  };
  if (!res.ok || !data.ok) {
    throw new Error(data.reason || `That did not save (${res.status}).`);
  }
  return data;
}

/** One answer per signed-in account per page load; a failure is not kept. */
let ownSignInsAnswer: { uid: string; promise: Promise<string[]> } | null = null;

/**
 * EVERY SIGN-IN THE SIGNED-IN ACCOUNT HOLDS, exactly as classPostRecord.mjs
 * counts them when it decides whether a class post is hers (its ownSignIns
 * action runs the same shared alias helper as its edit path).
 *
 * Why the class page asks the server (review follow-up on #1390, 2026-10-19):
 * a Jr Staff's email account cannot read her own UNPROMOTED dancer sign-in,
 * because that users doc has status 'dancer' and the users read rule lets
 * staff read only active staff accounts. So the browser alone never knew that
 * uid was hers, and a post she wrote on her dancer sign-in had no Edit or
 * Delete on her staff account. One person, either sign-in (DECISIONS 26, 27).
 *
 * Only decides which buttons to draw; the server re-checks every change.
 * Throws on failure, which useOwnSignInUids reads as "her own uid only".
 */
export function loadClassPostOwnSignInUids(): Promise<string[]> {
  const uid = auth?.currentUser?.uid ?? '';
  if (ownSignInsAnswer && uid && ownSignInsAnswer.uid === uid) return ownSignInsAnswer.promise;
  const promise = (postToRecordFn({ action: 'ownSignIns' }) as Promise<{ uids?: unknown }>)
    .then((data) => (Array.isArray(data.uids)
      ? data.uids.filter((value): value is string => typeof value === 'string' && value.length > 0)
      : []));
  if (uid) {
    const entry = { uid, promise };
    ownSignInsAnswer = entry;
    promise.catch(() => {
      if (ownSignInsAnswer === entry) ownSignInsAnswer = null;
    });
  }
  return promise;
}

export interface RetractBroadcastResult {
  /** Class boards taken down by this run. */
  removed: number;
  /** Boards that were already down, so a retry finishes rather than errors. */
  skipped: number;
  failures: string[];
  notificationsCleared: number;
  /** False when something is still live, so the card must not read as done. */
  recordStamped: boolean;
}

/**
 * Take a whole one-tap send back in a single request.
 *
 * Un-posting a watch week means ~70 posts. Doing it from the browser one at a
 * time left the button busy for the best part of a minute and, if the phone
 * slept or the connection dropped partway, split families across two states.
 * The server now does the whole fan-out, the notification cleanup and the
 * calendar overlay in one invocation, and stamps the record only when nothing
 * is still live. Director only.
 */
export async function retractBroadcastPosts(
  broadcastId: string,
  weekStart: string
): Promise<RetractBroadcastResult> {
  const data = (await postToRecordFn({
    action: 'retractBroadcast',
    broadcastId,
    weekStart,
  })) as Partial<RetractBroadcastResult>;
  return {
    removed: data.removed ?? 0,
    skipped: data.skipped ?? 0,
    failures: Array.isArray(data.failures) ? data.failures : [],
    notificationsCleared: data.notificationsCleared ?? 0,
    recordStamped: Boolean(data.recordStamped),
  };
}

/**
 * Reword a class post, keeping the version that was replaced.
 *
 * Goes through the server (see callClassPostRecord): the outgoing wording and
 * the new wording are written in one transaction, so an edit can never quietly
 * overwrite what families already read. Author or Director only, checked
 * against the stored post. A post that has been taken back is frozen.
 */
export async function editClassPost(
  id: string,
  body: string,
  options: { family?: boolean } = {}
): Promise<ClassPostRecordResult> {
  const trimmed = body.trim();
  // Measure the words, never the envelope. A formatted post serialises to
  // several times its visible length, and the server refuses rather than
  // truncating (netlify/functions/classPostRecord.mjs), so say so here first.
  // A family editor gets the family numbers, the ones the server holds her to.
  const maxVisible = options.family === true ? CLASS_POST_FAMILY_MAX_BODY : CLASS_POST_MAX_BODY;
  const maxStored = options.family === true
    ? CLASS_POST_FAMILY_MAX_STORED_BODY
    : CLASS_POST_MAX_STORED_BODY;
  if (!plainMessageText(trimmed).trim()) throw new Error('Write something first.');
  if (trimmed.length > maxStored || plainMessageText(trimmed).length > maxVisible) {
    throw new Error('That post is too long to save.');
  }
  return callClassPostRecord('edit', id, trimmed);
}

/**
 * Take a class post back without destroying it.
 *
 * The server empties expectedRecipientUids, which is what actually drops the
 * post out of every family's feed - family reads are an array-contains on that
 * field and the read rule checks the same list, so this is enforced rather
 * than filtered in the client. The body, the author, the time it went out and
 * the people it reached all survive for staff to read afterwards, and the
 * per-family alert rows carrying the withdrawn wording are cleared too, which
 * a browser could never do (their delete rule allows only the recipient).
 *
 * Idempotent from the caller's point of view: retracting an already-retracted
 * post reports the post as unchanged rather than throwing.
 */
export async function retractClassPost(id: string): Promise<ClassPostRecordResult> {
  try {
    return await callClassPostRecord('retract', id);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (message.includes('taken back')) {
      return { changed: false, notificationsCleared: 0 };
    }
    throw e;
  }
}

/** Every kept version of one post, oldest change first. Director-readable only. */
export function useClassPostRevisions(postId: string | undefined): {
  loading: boolean;
  revisions: ClassPostRevisionDoc[];
  error: string | null;
} {
  const { user } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const userUid = user?.uid;
  const userRole = userDoc?.role;
  const userStatus = userDoc?.status;
  const [revisions, setRevisions] = useState<ClassPostRevisionDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedRevisionsKey, setResolvedRevisionsKey] = useState<string | null>(null);
  const activeRevisionsKey = !meLoading && db && postId && userUid && userRole
    && userStatus === 'active' && userRole === 'director'
    ? JSON.stringify([postId, userUid, userRole, userStatus])
    : null;

  useEffect(() => {
    if (meLoading) return;
    if (!activeRevisionsKey || !db || !postId) {
      setRevisions([]);
      setResolvedRevisionsKey(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'classPosts', postId, 'revisions'),
      orderBy('changedAt', 'asc')
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        setRevisions(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ClassPostRevisionDoc, 'id'>) }))
        );
        setError(null);
        setResolvedRevisionsKey(activeRevisionsKey);
        setLoading(false);
      },
      (err) => {
        setRevisions([]);
        setError(err.message);
        setResolvedRevisionsKey(activeRevisionsKey);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [activeRevisionsKey, meLoading, postId]);

  const awaitingActiveRevisions = activeRevisionsKey !== null
    && resolvedRevisionsKey !== activeRevisionsKey;
  if (activeRevisionsKey === null) {
    return { loading: meLoading, revisions: [], error: null };
  }
  return {
    loading: loading || awaitingActiveRevisions,
    revisions: awaitingActiveRevisions ? [] : revisions,
    error: awaitingActiveRevisions ? null : error,
  };
}

/** How far back the staff history view looks. Bobbie asked for "say 12 months". */
export const CLASS_POST_HISTORY_MONTHS = 12;

/**
 * Posts that have been edited or taken back, most recent change first.
 *
 * Ranged and ordered on the same single field, so no composite index is
 * needed. Posts that were never touched have no lastChangedAt and are
 * correctly absent. Director-readable only.
 */
export function useChangedClassPosts(maxPosts = 200): UseClassPostsResult {
  const { user } = useAuth();
  const { userDoc, loading: meLoading } = useUserDoc();
  const userUid = user?.uid;
  const userRole = userDoc?.role;
  const userStatus = userDoc?.status;
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedHistoryKey, setResolvedHistoryKey] = useState<string | null>(null);
  const activeHistoryKey = !meLoading && db && userUid && userRole === 'director'
    && userStatus === 'active'
    ? JSON.stringify([userUid, userRole, userStatus, maxPosts])
    : null;

  useEffect(() => {
    if (meLoading) return;
    if (!activeHistoryKey || !db) {
      setPosts([]);
      setError(null);
      setResolvedHistoryKey(null);
      setLoading(false);
      return;
    }
    const since = new Date();
    since.setMonth(since.getMonth() - CLASS_POST_HISTORY_MONTHS);
    setLoading(true);
    setError(null);
    setPosts([]);
    const q = query(
      collection(db, 'classPosts'),
      where('lastChangedAt', '>=', FirestoreTimestamp.fromDate(since)),
      orderBy('lastChangedAt', 'desc'),
      limitDocs(maxPosts)
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        setPosts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) })));
        setError(null);
        setResolvedHistoryKey(activeHistoryKey);
        setLoading(false);
      },
      (err) => {
        setPosts([]);
        setError(err.message);
        setResolvedHistoryKey(activeHistoryKey);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [activeHistoryKey, maxPosts, meLoading]);

  const awaitingActiveHistory = activeHistoryKey !== null
    && resolvedHistoryKey !== activeHistoryKey;
  if (activeHistoryKey === null) {
    return { loading: meLoading, posts: [], error: null };
  }
  return {
    loading: loading || awaitingActiveHistory,
    posts: awaitingActiveHistory ? [] : posts,
    error: awaitingActiveHistory ? null : error,
  };
}

// classPostAckExempt and classPostAckPending now live in classPostAckCore.ts
// (a pure, framework-free file familyWaitingOnYouCore.ts can import without
// pulling in firebase/firestore - see that file's own header, and D4 P1-2,
// docs/audits/refute-2026-09-16/REFUTE_msg2_R2.md) and are re-exported here
// unchanged, so every existing `from './classPosts'` import keeps working.
export { classPostAckExempt, classPostAckPending };

/**
 * The people this post actually ASKED, which is its recipient list minus
 * everyone the repair added afterwards. Every ack tally, roster and chase
 * list counts against this, never against expectedRecipientUids: a family
 * who joined the class in March must not turn February's "3 of 12 said got
 * it" into "3 of 13" or appear under "Still needed" on Kim's screen.
 *
 * Reading permission is a separate question and stays with
 * expectedRecipientUids, which is what the Firestore rules read.
 */
export function classPostAckAudienceUids(post: ClassPostDoc, recipientUids?: readonly string[]): string[] {
  const exempt = post.backfilledRecipientUids ?? [];
  const base = recipientUids ?? post.expectedRecipientUids ?? [];
  return exempt.length === 0 ? [...base] : base.filter((uid) => !exempt.includes(uid));
}

/** Stamp my acknowledgement on a post. Sets only my own slot in acks{}. */
export async function ackClassPost(postId: string, uid: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'classPosts', postId), {
    [`acks.${uid}`]: serverTimestamp(),
  });
}

/** The shared reaction set. Kept as a re-export for older imports. */
// Keep this local for the class-post delivery bundle, which is also evaluated
// as one isolated module in the delivery safety tests.
export const REACTION_EMOJI = ['❤️', '👍', '👎', '😆', '🎉', '👏', '💃', '🕺'] as const;

/**
 * Set or clear the caller's reaction on a class post. A reaction counts as
 * the acknowledgment (Bobbie's rule), so when the post needs an ack from
 * this user the same write stamps their ack slot. Connected tasks are
 * separate docs and stay untouched - a reaction never completes a task.
 */
export async function reactToClassPost(
  postId: string,
  uid: string,
  emoji: string | null,
  alsoAck: boolean
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'classPosts', postId), {
    [`reactions.${uid}`]: emoji ?? deleteField(),
    ...(alsoAck && emoji ? { [`acks.${uid}`]: serverTimestamp() } : {}),
  });
}

/** Stamp my dismissal on a post. Sets only my own slot in dismissedBy{}. */
export async function dismissClassPost(postId: string, uid: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'classPosts', postId), {
    [`dismissedBy.${uid}`]: serverTimestamp(),
  });
}

/**
 * Post a reply through the authenticated, App-Check-protected server gateway.
 * The gateway owns idempotency and stamps public lastReplyAt only for a
 * whole-class reply. Displayed counts come from the readable reply snapshots,
 * never from a client-writable parent total.
 *
 * visibility defaults to 'class' (whole class can see it). Pass 'teacher'
 * for "just the teacher and Ms. Kim" or 'director' for "just Ms. Kim",
 * per Bobbie's decision.
 */
export function useReplyToClassPost() {
  const { user } = useAuth();
  return async (
    postId: string,
    text: string,
    visibility: ReplyVisibility = 'class',
    options: { replyId?: string; attachments?: MessageAttachment[] } = {}
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = text.trim();
    const attachments = options.attachments ?? [];
    const lengthError = classPostReplyTextError(trimmed);
    if (lengthError) throw new Error(lengthError);
    if (!trimmed && attachments.length === 0) throw new Error('Empty reply.');
    const firestore = db;
    const parentRef = doc(firestore, 'classPosts', postId);
    const replyRef = options.replyId
      ? doc(firestore, 'classPosts', postId, 'replies', options.replyId)
      : doc(collection(firestore, 'classPosts', postId, 'replies'));

    const result = await saveClassPostReplyMessage({
      user,
      targetId: postId,
      writeId: replyRef.id,
      attachments,
      payload: { text: trimmed, visibility },
    });
    const stampMyAck = result.stampMyAck === true;

    // The acknowledgement stays a separate best-effort client write through
    // the existing own-acks rule. The server has already saved the reply, so
    // an ack failure must not misreport the send itself as failed.
    if (stampMyAck) {
      try {
        await updateDoc(parentRef, { [`acks.${user.uid}`]: serverTimestamp() });
      } catch {
        // best-effort
      }
    }

    // Whole-class bells and off-app channels are now one durable server job
    // created atomically by the reply gateway. Restricted replies create no
    // job. The browser deliberately makes no second notify request, so a
    // saved reply cannot lose its only retry path or race a duplicate send.
  };
}

export function newClassPostReplyId(postId: string): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'classPosts', postId, 'replies')).id;
}

/**
 * Outstanding class posts for the signed-in user. "Outstanding" =
 * ackRequired is true AND I haven't yet acked or dismissed, across all the
 * classes my dancers are enrolled in. Mirrors useMyOutstandingDiscussions.
 */
export function useMyOutstandingClassPosts(): {
  loading: boolean;
  count: number;
} {
  const { user, loading: authLoading } = useAuth();
  const userUid = user?.uid;
  const [posts, setPosts] = useState<ClassPostDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedUid, setResolvedUid] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!db || !userUid) {
      setPosts([]);
      setResolvedUid(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setPosts([]);
    const q = query(
      collection(db, 'classPosts'),
      where('expectedRecipientUids', 'array-contains', userUid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        setPosts(
          snap.docs.map(
            (d) => ({ id: d.id, ...(d.data() as Omit<ClassPostDoc, 'id'>) })
          )
        );
        setResolvedUid(userUid);
        setLoading(false);
      },
      () => {
        setPosts([]);
        setResolvedUid(userUid);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [authLoading, userUid]);

  const awaitingActiveUser = !!userUid && resolvedUid !== userUid;
  if (!userUid) return { loading: authLoading, count: 0 };
  const me = userUid;
  const count = posts.filter(
    (p) =>
      p.ackRequired &&
      // A post the enrollment repair handed her afterwards is readable, not
      // owed. See classPostAckExempt.
      !classPostAckExempt(p, me) &&
      !(p.acks && p.acks[me]) &&
      !(p.dismissedBy && p.dismissedBy[me])
  ).length;
  return {
    loading: loading || awaitingActiveUser,
    count: awaitingActiveUser ? 0 : count,
  };
}
